import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Warehouse, ShoppingCart } from "lucide-react";
import { CATEGORIES, OBJECT_DEFS } from "@/lib/objectDefs";
import { useEditor } from "@/lib/store";

function CategoryHeader({ open, label, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/60 transition-colors"
    >
      <div className="flex items-center gap-1.5">
        {open ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
      </div>
      <span className="font-mono text-[10px] text-muted-foreground/70">{count}</span>
    </button>
  );
}

export default function LibraryPanel() {
  const projectType = useEditor((s) => s.projectType);
  const setProjectType = useEditor((s) => s.setProjectType);
  const [openCats, setOpenCats] = useState({
    racks: true, mezzanine: true, mhe: true, obstacles: true,
    super_racks: true, super_cold: true, super_pos: true, super_misc: true,
  });
  const addObject = useEditor((s) => s.addObject);
  const addObstacle = useEditor((s) => s.addObstacle);

  // Filter categories by the active module. 'shared' categories always appear.
  const grouped = CATEGORIES
    .filter((c) => c.module === "shared" || c.module === projectType)
    .map((c) => ({
      ...c,
      items: Object.values(OBJECT_DEFS).filter((d) => d.category === c.id),
    }));

  return (
    <aside className="w-60 shrink-0 border-r border-border/60 bg-card flex flex-col min-h-0">
      <div className="px-3 py-2.5 border-b border-border/60">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">// library</div>
        <div className="text-sm font-semibold tracking-tight mt-0.5">Objects</div>
      </div>

      {/* Module switcher — quickly flip between warehouse & supermarket libraries */}
      <div className="px-2 pt-2 pb-1.5 border-b border-border/60">
        <div className="flex items-center bg-background/40 border border-border/60 rounded-sm p-0.5">
          {[
            { id: "warehouse", label: "Warehouse", icon: Warehouse },
            { id: "supermarket", label: "Supermarket", icon: ShoppingCart },
          ].map((opt) => {
            const active = projectType === opt.id;
            const Icon = opt.icon;
            return (
              <button
                key={opt.id}
                data-testid={`lib-module-${opt.id}`}
                onClick={() => setProjectType(opt.id)}
                className={`flex-1 h-7 flex items-center justify-center gap-1 rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-3 h-3" />
                <span>{opt.label.slice(0, 4)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {grouped.map((cat) => (
          <div key={cat.id} className="border-b border-border/40 last:border-0">
            <CategoryHeader
              label={cat.label}
              open={openCats[cat.id]}
              count={cat.items.length}
              onClick={() => setOpenCats((s) => ({ ...s, [cat.id]: !s[cat.id] }))}
            />
            {openCats[cat.id] && (
              <div className="pb-1.5">
                {cat.items.map((item) => (
                  <button
                    key={item.type}
                    data-testid={`add-${item.type}`}
                    onClick={() => {
                      if (cat.id === "obstacles") {
                        addObstacle(item.type, { x: 2000, y: 2000 });
                      } else {
                        addObject(item.type, { x: 2000, y: 2000 });
                      }
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-secondary/60 transition-colors group text-left"
                  >
                    <div
                      className="w-3.5 h-3.5 rounded-sm shrink-0 border border-white/10"
                      style={{ background: item.color }}
                    />
                    <span className="text-xs flex-1 truncate">{item.label}</span>
                    <Plus className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}
